// client/src/components/VerifyEmailPage.js

import React, { useEffect, useState } from "react";
import {
  useSearchParams,
  useNavigate,
  Link,
} from "react-router-dom";

import getApiBaseUrl from "../utils/api";

function VerifyEmailPage({ onLogin }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const verificationToken = searchParams.get("token");

  const [status, setStatus] = useState("verifying");
  const [message, setMessage] = useState(
    "Verifying your email..."
  );

  const API_URL = getApiBaseUrl();

  useEffect(() => {
    if (!verificationToken) {
      setStatus("error");
      setMessage("Invalid verification link.");
      return;
    }

    let isMounted = true;
    let redirectTimer = null;

    const verifyEmail = async () => {
      try {
        const response = await fetch(
          `${API_URL}/auth/verify-email`,
          {
            method: "POST",

            credentials: "include",

            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },

            body: JSON.stringify({
              token: verificationToken,
            }),
          }
        );

        let data = {};

        try {
          data = await response.json();
        } catch {
          data = {};
        }

        if (!isMounted) {
          return;
        }

        if (!response.ok) {
          setStatus("error");
          setMessage(
            data.msg ||
              data.message ||
              "Email verification failed."
          );
          return;
        }

        /*
         * IMPORTANT:
         *
         * Do NOT save data.token anywhere.
         *
         * The backend should set the authentication JWT as:
         *
         * Set-Cookie:
         *   authToken=...;
         *   HttpOnly;
         *   Secure;
         *   SameSite=Lax;
         *
         * JavaScript cannot access an HttpOnly cookie.
         */

        setStatus("success");
        setMessage(
          "Email verified successfully! Logging you in..."
        );

        // Tell App.js that authentication succeeded.
        if (typeof onLogin === "function") {
          onLogin();
        }

        redirectTimer = setTimeout(() => {
          if (isMounted) {
            navigate("/profile", { replace: true });
          }
        }, 2000);
      } catch (error) {
        console.error(
          "Email verification request failed:",
          error
        );

        if (isMounted) {
          setStatus("error");
          setMessage(
            "Unable to contact the server. Please try again."
          );
        }
      }
    };

    verifyEmail();

    return () => {
      isMounted = false;

      if (redirectTimer) {
        clearTimeout(redirectTimer);
      }
    };
  }, [
    verificationToken,
    navigate,
    onLogin,
    API_URL,
  ]);

  return (
    <div
      style={{
        minHeight: "80vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        background: "var(--bg-main)",
        color: "var(--text-main)",
        padding: "20px",
      }}
    >
      <div
        style={{
          background: "var(--bg-card)",
          padding: "3rem",
          borderRadius: "16px",
          textAlign: "center",
          boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
          maxWidth: "500px",
          width: "100%",
        }}
      >
        <div
          style={{
            fontSize: "4rem",
            marginBottom: "1rem",
          }}
        >
          {status === "verifying" && "⏳"}
          {status === "success" && "✅"}
          {status === "error" && "❌"}
        </div>

        <h2
          style={{
            marginBottom: "1rem",
          }}
        >
          {status === "verifying" && "Verifying..."}
          {status === "success" && "Success!"}
          {status === "error" && "Error"}
        </h2>

        <p
          style={{
            fontSize: "1.1rem",
            color: "var(--text-muted)",
            marginBottom: "2rem",
          }}
        >
          {message}
        </p>

        {status === "error" && (
          <Link
            to="/login"
            className="cta-btn"
          >
            Back to Login
          </Link>
        )}
      </div>
    </div>
  );
}

export default VerifyEmailPage;